import { Component, Prop, h } from '@stencil/core';

const COMPONENT_NAME = 'dh-navbar-type-1';

/**
 * Minimal DocHub site header — fixed-height white bar with a single brand logo link aligned to the start of a centered content row.
 * Full-bleed background with a `max-width:1208px` inner container; logo is wrapped in an anchor pointing to the brand homepage. An optional `menu` slot is reserved for future navigation items.
 * Differs from richer DocHub navbar variants by intentionally rendering brand only — used on form-landing pages where the in-page primary CTA replaces top navigation.
 *
 * @category DocHub
 * @slot menu - Optional trailing navigation block (e.g. dh-button list). Stays empty on form-landing layouts.
 * @example
 *   <dh-navbar-type-1
 *     logo-src="/dochub/logos/dochub.svg"
 *     logo-alt="DocHub"
 *     logo-href="/"
 *   ></dh-navbar-type-1>
 */
@Component({
  tag: 'dh-navbar-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhNavbarType1 {
  /** Brand logo image URL. */
  @Prop({ attribute: 'logo-src' }) logoSrc!: string;

  /**
   * Accessible alt text for the brand logo.
   * @content
   */
  @Prop({ attribute: 'logo-alt' }) logoAlt: string = 'DocHub logo';

  /** Home page URL the logo links to. */
  @Prop({ attribute: 'logo-href' }) logoHref: string = '/';

  /** Intrinsic logo width in pixels (preserved to avoid CLS). */
  @Prop({ attribute: 'logo-width' }) logoWidth: number = 104;

  /** Intrinsic logo height in pixels (preserved to avoid CLS). */
  @Prop({ attribute: 'logo-height' }) logoHeight: number = 24;

  /**
   * Accessible label for the header landmark.
   * @content
   */
  @Prop({ attribute: 'aria-label' }) ariaLabelText: string = 'Site header with logo and navigation';

  render() {
    return (
      <header class={COMPONENT_NAME} aria-label={this.ariaLabelText}>
        <div class={`${COMPONENT_NAME}__inner`}>
          <a class={`${COMPONENT_NAME}__brand`} href={this.logoHref} aria-label="Main page">
            <img
              class={`${COMPONENT_NAME}__logo`}
              src={this.logoSrc}
              alt={this.logoAlt}
              width={this.logoWidth}
              height={this.logoHeight}
            />
          </a>
          <div class={`${COMPONENT_NAME}__menu`}>
            <slot name="menu" />
          </div>
        </div>
      </header>
    );
  }
}
